/**
 * X ADS › TOOLS — /x-ads/tools?tab=lead-forms|audiences
 *
 * Lead forms: X's native forms, their sync state, "Sync now" (X has no lead webhook — leads
 * arrive through export jobs), and the stored leads with a CSV download.
 * Audiences: custom audiences, and a new one built from a CRM contact group.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDownToLine, ClipboardList, Plus, RefreshCw, Target, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import { DeleteButton } from '@/components/x-ads/DeleteButton';
import { RetryLine, XAdsNotice, XAdsShell } from '@/components/x-ads/XAdsShell';
import { XCheck } from '@/components/x-ads/XCheck';
import { XSelect } from '@/components/x-ads/XSelect';
import { useXConnection } from '@/lib/x-ads/connection';
import { dateOnly, dateTime, downloadCsv, label, num } from '@/lib/x-ads/format';
import { contactGroupsService, type ContactGroup } from '@/services/contact-groups.service';
import { LEAD_FIELD_KEYS, errMsg, xAdsService, type XAudience, type XLead, type XLeadForm, type XLeadFormInput } from '@/services/x-ads.service';

export default function XAdsTools() {
  const { uid } = useXConnection();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'audiences' ? 'audiences' : 'lead-forms';
  return <XAdsShell section="Tools" description="Lead forms and their leads, and custom audiences from your contact groups.">
    <div className="xa-subtabs" role="group" aria-label="Tools">
      <button type="button" aria-pressed={tab === 'lead-forms'} onClick={() => setParams({ tab: 'lead-forms' })}><ClipboardList size={14} />Lead forms</button>
      <button type="button" aria-pressed={tab === 'audiences'} onClick={() => setParams({ tab: 'audiences' })}><Target size={14} />Audiences</button>
    </div>
    {tab === 'lead-forms' ? <LeadForms uid={uid} /> : <Audiences uid={uid} />}
  </XAdsShell>;
}

// ── Lead forms ───────────────────────────────────────────────────────────────
function LeadForms({ uid }: { uid: string }) {
  const [forms, setForms] = useState<XLeadForm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // X has not opened the lead-form endpoints to this app (Standard access does not include them).
  const [noAccess, setNoAccess] = useState(false);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [leadsRevision, setLeadsRevision] = useState(0);
  const [formFilter, setFormFilter] = useState('');

  const load = useCallback(async () => {
    if (!uid) return;
    setError(null);
    try { setForms(await xAdsService.leadForms(uid)); setNoAccess(false); }
    catch (e) {
      const m = errMsg(e, 'Unable to load lead forms.');
      if (/does not have access to this Ads API endpoint/i.test(m)) { setNoAccess(true); setForms([]); }
      else setError(m);
    }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  const sync = async (f: XLeadForm) => {
    setSyncing(f.id);
    try {
      const r = await xAdsService.syncLeadForm(uid, f.id);
      if (r.status === 'done') toast.success(`${r.added ?? 0} new lead${r.added === 1 ? '' : 's'} from "${f.name ?? f.id}"`);
      else if (r.status === 'up_to_date') toast.success('Already up to date');
      else if (r.status === 'pending') toast('X is still preparing the export. It finishes on the next hourly run.');
      else if (r.status === 'failed') toast.error(r.error || 'The lead sync failed.');
      else toast('Nothing to sync for this form.');
      await load();
      setLeadsRevision((n) => n + 1);
    } catch { /* toasted */ }
    finally { setSyncing(null); }
  };

  return <div className="space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><h3 className="xa-panel-title">Lead forms</h3><p className="xa-muted">Leads are pulled every hour; "Sync now" pulls them at once. Exports need the Account administrator role on the ads account.</p></div>
      <div className="flex gap-2">
        <Button variant="ghost" onClick={load} iconLeft={<RefreshCw size={15} />}>Refresh</Button>
        <Button onClick={() => setCreating(true)} disabled={noAccess} iconLeft={<Plus size={15} />}>New lead form</Button>
      </div>
    </div>
    {noAccess && <XAdsNotice title="Lead forms are not enabled for this X app yet">
      X answers that this app has no access to the lead-form endpoints. Ads API Standard access does not include them: ask X
      (developer.x.com → Ads API access, or the X Developer Community) to enable lead forms for the app, then press Refresh.
      Campaigns, posts, audiences and reports work without it.
    </XAdsNotice>}
    {error && <RetryLine message={error} onRetry={load} />}
    {!forms && !error && <div className="skeleton h-40 rounded-2xl" aria-label="Loading lead forms" />}
    {forms?.length === 0 && !noAccess && <EmptyState icon={<ClipboardList size={28} />} title="No lead forms yet" description="A lead form collects contact details right inside X. Attach it to a post with a lead form card."
      action={<Button onClick={() => setCreating(true)} iconLeft={<Plus size={15} />}>New lead form</Button>} />}
    {!!forms?.length && <div className="xa-list">
      {forms.map((f) => <div key={f.id} className="xa-row !items-start">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{f.name || f.id}</span>
            {f.sync?.pending && <span className="xa-chip !bg-warning-soft !text-warning">Export in progress</span>}
            {f.sync && <span className="xa-chip">{num(f.sync.leadCount)} lead{f.sync.leadCount === 1 ? '' : 's'}</span>}
          </div>
          {f.purpose && <p className="xa-muted line-clamp-2">{f.purpose}</p>}
          <div className="mt-1 flex flex-wrap gap-1">{(f.fields ?? []).map((x, i) => <span key={`${x.key}-${i}`} className="xa-chip !text-[10px]">{label(String(x.key ?? ''))}{x.optional ? ' (optional)' : ''}</span>)}</div>
          <p className="xa-muted mt-1">
            {f.sync?.lastRunAt ? `Last sync ${dateTime(f.sync.lastRunAt)}` : 'Never synced'}
            {f.sync?.syncedUntil ? ` · leads up to ${dateTime(f.sync.syncedUntil)}` : ''} · Created {dateOnly(f.createdAt)} · ID {f.id}
          </p>
          {f.sync?.lastError && <p className="mt-1 text-xs text-danger">Last error: {f.sync.lastError}</p>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setFormFilter(f.id)}>View leads</Button>
        <Button variant="secondary" size="sm" loading={syncing === f.id} disabled={!!syncing} onClick={() => sync(f)} iconLeft={<RefreshCw size={13} />}>Sync now</Button>
        <DeleteButton what="lead form" name={f.name} onDelete={() => xAdsService.deleteLeadForm(uid, f.id)} onDone={load} />
      </div>)}
    </div>}
    <Leads uid={uid} forms={forms ?? []} formId={formFilter} onFormId={setFormFilter} revision={leadsRevision} />
    <LeadFormEditor uid={uid} open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
  </div>;
}

function Leads({ uid, forms, formId, onFormId, revision }: { uid: string; forms: XLeadForm[]; formId: string; onFormId: (id: string) => void; revision: number }) {
  const [leads, setLeads] = useState<XLead[] | null>(null);
  const [limit, setLimit] = useState(200);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!uid) return;
    setError(null);
    try { setLeads(await xAdsService.leads(uid, formId || undefined, limit)); }
    catch (e) { setError(errMsg(e, 'Unable to load leads.')); }
  }, [uid, formId, limit]);
  useEffect(() => { load(); }, [load, revision]);

  const formName = (id?: string) => forms.find((f) => f.id === id)?.name ?? id ?? '';
  const extraKeys = useMemo(() => {
    const keys = new Set<string>();
    (leads ?? []).forEach((l) => Object.keys(l.fields ?? {}).forEach((k) => keys.add(k)));
    return Array.from(keys);
  }, [leads]);
  const exportCsv = () => downloadCsv(`x-leads${formId ? `-${formId}` : ''}.csv`, [
    ['Received', 'Submitted', 'Name', 'Email', 'Phone', 'Lead form', ...extraKeys],
    ...(leads ?? []).map((l) => [l.receivedAt, l.submittedAt, l.profileName, l.email, l.phone, formName(l.leadFormId), ...extraKeys.map((k) => l.fields?.[k])]),
  ]);

  return <Card padded={false} className="overflow-hidden">
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
      <div><h3 className="xa-panel-title">Leads</h3><p className="xa-muted">Stored in the CRM after each sync, newest first.</p></div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-52"><XSelect value={formId} onChange={onFormId} clearable options={[{ value: '', label: 'All lead forms' }, ...forms.map((f) => ({ value: f.id, label: f.name || f.id }))]} /></div>
        <div className="w-36"><XSelect value={String(limit)} onChange={(v) => v && setLimit(Number(v))} numberItems={false} options={[100, 200, 500, 1000].map((n) => ({ value: String(n), label: `Latest ${n}` }))} /></div>
        <Button variant="secondary" size="sm" onClick={exportCsv} disabled={!leads?.length} iconLeft={<ArrowDownToLine size={14} />}>Download CSV</Button>
      </div>
    </div>
    {error && <div className="p-4"><RetryLine message={error} onRetry={load} /></div>}
    {!leads && !error && <div className="skeleton m-4 h-24 rounded-xl" aria-label="Loading leads" />}
    {leads?.length === 0 && <p className="p-8 text-center text-sm text-text-muted">No leads yet{formId ? ' for this form' : ''}. Press "Sync now" on a form to pull the latest.</p>}
    {!!leads?.length && <div className="overflow-x-auto">
      <table className="xa-table">
        <caption className="sr-only">Leads from X lead forms</caption>
        <thead className="border-b border-border bg-surface-alt/60"><tr>{['Received', 'Name', 'Email', 'Phone', 'Lead form', 'Other answers'].map((h) => <th key={h} scope="col" className="!text-left">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-border">
          {leads.map((l, i) => <tr key={l.leadUid ?? i}>
            <td className="!text-left">{dateTime(l.receivedAt)}</td>
            <td className="!text-left">{l.profileName || '—'}</td>
            <td className="!text-left">{l.email || '—'}</td>
            <td className="!text-left">{l.phone || '—'}</td>
            <td className="!text-left">{formName(l.leadFormId)}</td>
            <td className="max-w-[360px] truncate !text-left !whitespace-nowrap" title={Object.entries(l.fields ?? {}).map(([k, v]) => `${k}: ${v}`).join('\n')}>
              {Object.entries(l.fields ?? {}).map(([k, v]) => `${label(k)}: ${v}`).join(' · ') || '—'}
            </td>
          </tr>)}
        </tbody>
      </table>
    </div>}
  </Card>;
}

const DEFAULT_FIELDS: Record<string, boolean> = { FULL_NAME: false, EMAIL: false, PHONE_NUMBER: true };

function LeadFormEditor({ uid, open, onClose, onCreated }: { uid: string; open: boolean; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  /** key → optional */
  const [fields, setFields] = useState<Record<string, boolean>>(DEFAULT_FIELDS);
  const [ppUrl, setPpUrl] = useState('https://');
  const [ppCompany, setPpCompany] = useState('');
  const [introHeadline, setIntroHeadline] = useState('');
  const [introDescription, setIntroDescription] = useState('');
  const [finalHeadline, setFinalHeadline] = useState('');
  const [finalDescription, setFinalDescription] = useState('');
  const [ctaTitle, setCtaTitle] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const check = (): string | null => {
    if (!name.trim()) return 'Give the form a name.';
    if (!purpose.trim()) return 'Say what the form is for.';
    const keys = Object.keys(fields);
    if (keys.length === 0) return 'Add at least one field.';
    if (keys.every((k) => fields[k])) return 'At least one field must be required.';
    if (!/^https:\/\/\S+\.\S+/i.test(ppUrl.trim())) return 'The privacy policy must be an https:// address.';
    if (!ppCompany.trim()) return 'Enter the company name shown with the privacy policy.';
    if (introHeadline.length > 190 || finalHeadline.length > 190) return 'Headlines must be 190 characters or fewer.';
    if ((ctaTitle.trim() || ctaUrl.trim()) && !finalHeadline.trim()) return 'The thank-you button needs a thank-you headline.';
    if ((ctaTitle.trim() || ctaUrl.trim()) && (!ctaTitle.trim() || !ctaUrl.trim())) return 'The thank-you button needs both a title and a URL.';
    if (ctaUrl.trim() && !/^https:\/\//i.test(ctaUrl.trim())) return 'The thank-you button URL must be https://.';
    return null;
  };

  const save = async () => {
    const p = check();
    setProblem(p);
    if (p) return;
    const body: XLeadFormInput = {
      name: name.trim(), purpose: purpose.trim(),
      fields: LEAD_FIELD_KEYS.filter((k) => k in fields).map((k) => ({ key: k, optional: fields[k] })),
      privacyPolicy: { url: ppUrl.trim(), companyName: ppCompany.trim() },
    };
    if (introHeadline.trim()) body.introduction = { headline: introHeadline.trim(), description: introDescription.trim() || undefined };
    if (finalHeadline.trim()) {
      body.finalPage = { headline: finalHeadline.trim(), description: finalDescription.trim() || undefined };
      if (ctaTitle.trim() && ctaUrl.trim()) body.finalPage.ctaButton = { title: ctaTitle.trim(), url: ctaUrl.trim() };
    }
    setSaving(true);
    try {
      await xAdsService.createLeadForm(uid, body);
      toast.success('Lead form created on X');
      setName(''); setPurpose(''); setFields(DEFAULT_FIELDS); setIntroHeadline(''); setIntroDescription(''); setFinalHeadline(''); setFinalDescription(''); setCtaTitle(''); setCtaUrl('');
      onCreated();
    } catch (e) { setProblem(errMsg(e)); }
    finally { setSaving(false); }
  };

  return <Modal open={open} onClose={onClose} title="New lead form" widthClass="max-w-3xl">
    <div className="grid gap-x-6 md:grid-cols-2">
      <FormField label="Form name" htmlFor="lf-name" required><Input id="lf-name" maxLength={255} value={name} onChange={(e) => setName(e.target.value)} /></FormField>
      <FormField label="Company name" htmlFor="lf-co" required hint="Shown with the privacy policy"><Input id="lf-co" maxLength={255} value={ppCompany} onChange={(e) => setPpCompany(e.target.value)} /></FormField>
      <div className="md:col-span-2"><FormField label="Purpose" htmlFor="lf-purpose" required hint="Why you collect these details"><Textarea id="lf-purpose" rows={2} maxLength={1000} value={purpose} onChange={(e) => setPurpose(e.target.value)} /></FormField></div>
      <div className="md:col-span-2"><FormField label="Privacy policy URL" htmlFor="lf-pp" required><Input id="lf-pp" type="url" value={ppUrl} onChange={(e) => setPpUrl(e.target.value)} /></FormField></div>
    </div>
    <FormField label="Fields" hint="Tick a field to ask for it; at least one must be required">
      <div className="grid gap-1.5 sm:grid-cols-2">
        {LEAD_FIELD_KEYS.map((k) => {
          const on = k in fields;
          return <div key={k} className={`xa-option !py-1.5 ${on ? 'is-on' : ''}`}>
            <label className="flex flex-1 items-center gap-2">
              <XCheck checked={on} onChange={(c) => setFields((f) => { const n = { ...f }; if (c) n[k] = false; else delete n[k]; return n; })} />
              {label(k)}
            </label>
            {on && <label className="flex items-center gap-1 text-xs text-text-muted"><XCheck checked={fields[k]} onChange={(c) => setFields((f) => ({ ...f, [k]: c }))} />Optional</label>}
          </div>;
        })}
      </div>
    </FormField>
    <div className="grid gap-x-6 md:grid-cols-2">
      <FormField label="Intro headline" htmlFor="lf-ih" hint="Optional"><Input id="lf-ih" maxLength={190} value={introHeadline} onChange={(e) => setIntroHeadline(e.target.value)} /></FormField>
      <FormField label="Intro text" htmlFor="lf-id" hint="Optional"><Input id="lf-id" value={introDescription} onChange={(e) => setIntroDescription(e.target.value)} disabled={!introHeadline.trim()} /></FormField>
      <FormField label="Thank-you headline" htmlFor="lf-fh" hint="Optional"><Input id="lf-fh" maxLength={190} value={finalHeadline} onChange={(e) => setFinalHeadline(e.target.value)} /></FormField>
      <FormField label="Thank-you text" htmlFor="lf-fd" hint="Optional"><Input id="lf-fd" value={finalDescription} onChange={(e) => setFinalDescription(e.target.value)} disabled={!finalHeadline.trim()} /></FormField>
      <FormField label="Thank-you button title" htmlFor="lf-ct" hint="Optional"><Input id="lf-ct" value={ctaTitle} onChange={(e) => setCtaTitle(e.target.value)} disabled={!finalHeadline.trim()} /></FormField>
      <FormField label="Thank-you button URL" htmlFor="lf-cu" hint="https://"><Input id="lf-cu" type="url" value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} disabled={!finalHeadline.trim()} /></FormField>
    </div>
    {problem && <p role="alert" className="mb-3 text-sm text-danger">{problem}</p>}
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={save} loading={saving}>Create lead form</Button>
    </div>
  </Modal>;
}

// ── Audiences ────────────────────────────────────────────────────────────────
function Audiences({ uid }: { uid: string }) {
  const [audiences, setAudiences] = useState<XAudience[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    if (!uid) return;
    setError(null);
    try { setAudiences(await xAdsService.audiences(uid)); }
    catch (e) { setError(errMsg(e, 'Unable to load audiences.')); }
  }, [uid]);
  useEffect(() => { load(); }, [load]);

  return <div className="space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div><h3 className="xa-panel-title">Custom audiences</h3><p className="xa-muted">Emails and phone numbers are hashed before they leave the CRM.</p></div>
      <div className="flex gap-2">
        <Button variant="ghost" onClick={load} iconLeft={<RefreshCw size={15} />}>Refresh</Button>
        <Button onClick={() => setCreating(true)} iconLeft={<Plus size={15} />}>From a contact group</Button>
      </div>
    </div>
    <XAdsNotice title="X needs at least 100 matched users">Matching runs on X over the next hours. An audience under 100 matched users cannot be targeted yet.</XAdsNotice>
    {error && <RetryLine message={error} onRetry={load} />}
    {!audiences && !error && <div className="skeleton h-40 rounded-2xl" aria-label="Loading audiences" />}
    {audiences?.length === 0 && <EmptyState icon={<Users size={28} />} title="No custom audiences yet" description="Build one from a CRM contact group and use it to include or exclude people in a line item's targeting."
      action={<Button onClick={() => setCreating(true)} iconLeft={<Plus size={15} />}>From a contact group</Button>} />}
    {!!audiences?.length && <Card padded={false} className="overflow-x-auto">
      <table className="xa-table">
        <caption className="sr-only">Custom audiences</caption>
        <thead className="border-b border-border bg-surface-alt/60"><tr>{['Audience', 'Type', 'Size', 'Targetable', 'Created'].map((h) => <th key={h} scope="col">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-border">
          {audiences.map((a) => <tr key={a.id}>
            <td><p className="font-semibold">{a.name || a.id}</p><p className="text-[11px] text-text-muted">{a.description ? `${a.description} · ` : ''}ID {a.id}</p></td>
            <td>{label(a.audienceType)}</td>
            <td>{a.audienceSize !== undefined ? num(a.audienceSize) : '—'}</td>
            <td className="!whitespace-normal">{a.targetable ? <span className="text-success">Yes</span> : <span className="text-warning">{a.reasonsNotTargetable?.length ? a.reasonsNotTargetable.map(label).join(', ') : 'Not yet'}</span>}</td>
            <td>{dateOnly(a.createdAt)}</td>
          </tr>)}
        </tbody>
      </table>
    </Card>}
    <AudienceFromGroup uid={uid} open={creating} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />
  </div>;
}

function AudienceFromGroup({ uid, open, onClose, onCreated }: { uid: string; open: boolean; onClose: () => void; onCreated: () => void }) {
  const [groups, setGroups] = useState<ContactGroup[] | null>(null);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [groupUid, setGroupUid] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!open || groups) return;
    contactGroupsService.list().then(setGroups).catch((e) => setGroupError(errMsg(e, 'Could not load contact groups.')));
  }, [open, groups]);

  const save = async () => {
    if (!groupUid) { setProblem('Choose a contact group.'); return; }
    if (!name.trim()) { setProblem('Give the audience a name.'); return; }
    setProblem(null); setSaving(true);
    try {
      const r = await xAdsService.audienceFromGroup(uid, { groupUid, name: name.trim(), description: description.trim() || undefined });
      toast.success(`Audience created: ${num(r.usersUploaded)} of ${num(r.contactsInGroup)} contacts uploaded`);
      setGroupUid(''); setName(''); setDescription('');
      onCreated();
    } catch (e) { setProblem(errMsg(e)); }
    finally { setSaving(false); }
  };

  return <Modal open={open} onClose={onClose} title="Audience from a contact group">
    {groupError && <XAdsNotice title="Contact groups unavailable" error>{groupError}</XAdsNotice>}
    <FormField label="Contact group" htmlFor="au-group" required>
      <XSelect id="au-group" value={groupUid} disabled={!groups?.length} onChange={(v) => {
        setGroupUid(v);
        const g = groups?.find((x) => x.groupUid === v);
        if (g && !name.trim()) setName(g.name);
      }} placeholder={groups ? groups.length ? 'Choose a group' : 'No contact groups yet' : 'Loading…'}
        options={(groups ?? []).map((g) => ({ value: g.groupUid, label: `${g.name}${g.contactCount !== undefined ? ` (${num(g.contactCount)})` : ''}` }))} />
    </FormField>
    <FormField label="Audience name" htmlFor="au-name" required><Input id="au-name" maxLength={255} value={name} onChange={(e) => setName(e.target.value)} /></FormField>
    <FormField label="Description" htmlFor="au-desc" hint="Optional"><Textarea id="au-desc" rows={2} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} /></FormField>
    <p className="xa-muted mb-3">Contacts need an email, or a phone number with its country code. X needs at least 100 matched users before the audience can be targeted.</p>
    {problem && <p role="alert" className="mb-3 text-sm text-danger">{problem}</p>}
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={save} loading={saving}>Create audience</Button>
    </div>
  </Modal>;
}
